import { AppleIcon } from "@/components/Icons/AppleIcon";
import { GoogleIcon } from "@/components/Icons/GoogleIcon";
import { SignupErrorFeedback } from "../SignupErrorFeedback/SignupErrorFeedback";
import { ucfirst } from "@/utils/string";
import { IoIcon } from "@/components/Icons/IoIcon";
import { CarouselSlide } from "@/components/Common/CarouselForm/CarouselForm";
import { TwitterIcon } from "@/components/Icons/TwitterIcon";
import { AuthMachineContext } from "@/store/machine/authMachine/authMachine";
import { AuthEventType } from "@/types/auth";
import { useLocation, useRoute } from "wouter";
import { useEffect, useState } from "react";
import { cn } from "@/utils/styles";
import { Loader } from "@/components/Common/Loader/Loader";
import { TermsAndConditionsModal } from "@/components/Common/TermsAndConditionsModal/TermsAndConditionsModal";
import env from "@/env";
import { WorldCoin } from "@/components/Icons/WorldCoinIcon";
import { AnalyticsEventType } from "@/constants/analytics";
import { trackEvent } from "@/utils/analytics";

export const LoginStep: React.FC<CarouselSlide> = () => {
  const [, pageParams] = useRoute("/login/:section");
  const isSignup = pageParams?.section === "signup";

  const authActorRef = AuthMachineContext.useActorRef();
  const failedError = AuthMachineContext.useSelector((state) => state.context.failedError);
  const ssoType = AuthMachineContext.useSelector((state) => state.context.ssoType);
  const [, setLocation] = useLocation();
  const [isSubmitting, setSubmitting] = useState(false);
  const [isTermsAndConditionsOpen, setIsTermsAndConditionsOpen] = useState(false);

  useEffect(() => {
    setSubmitting(false);
  }, [failedError]);

  useEffect(() => {
    const pageVisibilityHandler = () => {
      setSubmitting(false);
    };

    window.addEventListener("pagehide", pageVisibilityHandler, false);
    return () => {
      window.removeEventListener("pagehide", pageVisibilityHandler);
    };
  }, []);

  return (
    <>
      <TermsAndConditionsModal
        isOpen={isTermsAndConditionsOpen}
        onClose={() => {
          setIsTermsAndConditionsOpen(false);
        }}
      />
      <div className="flex flex-1 flex-col items-center justify-center">
        <div className="flex w-[300px] flex-col gap-5 py-6 text-center">
          <div className="flex flex-row justify-center">
            <div className="flex size-[64px] flex-row items-center justify-center rounded-lg border border-gray-dark-900 bg-gray-dark-600">
              <IoIcon className="h-[16px] text-white" />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <h2 className="title-h4 text-white">
              One ID
              <br />
              Unlimited Access
            </h2>
            <p className="subtext-x-small text-gray-dark-1000">One account for everything IO</p>
          </div>

          {failedError && <SignupErrorFeedback>{ucfirst(failedError)}</SignupErrorFeedback>}
          <div className="flex flex-col gap-2">
            {LOGIN_OPTIONS.map((option) => {
              const { value, label } = option;

              return (
                <a
                  key={value}
                  className={cn(
                    `subtext-medium flex flex-row items-center justify-center gap-3 rounded border border-gray-dark-1100 px-4 py-3 text-white transition-all hover:border-white hover:bg-white hover:text-black`,
                    isSubmitting ? `pointer-events-none` : `cursor-pointer`
                  )}
                  onClick={async () => {
                    trackEvent(
                      isSignup
                        ? AnalyticsEventType.SIGN_UP_INITIATED
                        : AnalyticsEventType.SIGNED_IN,
                      {
                        type: value
                      }
                    );
                    setSubmitting(true);

                    window.setTimeout(() => {
                      authActorRef.send({
                        type: AuthEventType.SET_SSO_TYPE,
                        value
                      });
                    }, 200);
                  }}
                >
                  <option.icon className="h-[24px]" />
                  {`Sign ${isSignup ? "up" : "in"} with ${label}`}
                  {isSubmitting && value === ssoType && (
                    <Loader className="!h-[14px] !w-[14px]" color="#fff" />
                  )}
                </a>
              );
            })}
          </div>

          {isSignup ? (
            <p className="subtext-small text-gray-dark-1000">
              Already a Member?{" "}
              <a
                onClick={() => setLocation("/login")}
                className="cursor-pointer text-white hover:underline"
              >
                Sign In Here
              </a>
            </p>
          ) : (
            <p className="subtext-small text-gray-dark-1000">
              Don’t have an account?{" "}
              <a
                onClick={() => {
                  trackEvent(AnalyticsEventType.SIGN_UP_CLICKED);
                  setLocation("/login/signup");
                }}
                className="cursor-pointer text-white hover:underline"
              >
                Sign Up Here
              </a>
            </p>
          )}
        </div>
      </div>
    </>
  );
};

const LOGIN_OPTIONS = [
  {
    value: "google-oauth2",
    label: "Google",
    icon: GoogleIcon
  },
  {
    value: "apple",
    label: "Apple",
    icon: AppleIcon
  },
  {
    value: "twitter",
    label: "Twitter",
    icon: TwitterIcon
  },
  {
    value: "worldcoin",
    label: "Worldcoin",
    icon: WorldCoin
  }
].filter(({ value }) => {
  if (!env.LOGIN_OPTIONS) {
    return true;
  }

  return env.LOGIN_OPTIONS.indexOf(value) > -1;
});
